import { useCallback, useEffect, useState } from 'react';
import { Input } from 'components/Input';
import { Textarea } from 'components/Textarea';
import { lineStrings } from 'constants/strings';
import { cn } from 'utils/cn';
import {
  ERichMenuAreaActionType,
  RICH_MENU_ACTION_LABEL_MAX_LENGTH,
  RICH_MENU_MESSAGE_ACTION_TEXT_MAX_LENGTH,
  RICH_MENU_URI_MAX_LENGTH,
} from 'types';
import type {
  ERichMessageAreaActionType,
  TRichMessageAreaAction,
} from 'types/richMessage';
import type { TRichMessageAreaFieldErrors } from './validateRichMessageCreate';

type Props = {
  areaKeys: string[];
  areas: TRichMessageAreaAction[];
  onChange: (index: number, next: TRichMessageAreaAction) => void;
  fieldErrors?: Record<number, TRichMessageAreaFieldErrors>;
  expandAreaSignal?: { key: number; index: number } | null;
};

const strings = lineStrings.management.richMessage;

const actionOptions: ERichMessageAreaActionType[] = [
  ERichMenuAreaActionType.None,
  ERichMenuAreaActionType.Uri,
  ERichMenuAreaActionType.Message,
];

function optionLabel(type: ERichMessageAreaActionType): string {
  switch (type) {
    case ERichMenuAreaActionType.None:
      return strings.actionTypeOptionNone;
    case ERichMenuAreaActionType.Uri:
      return strings.actionTypeOptionUri;
    case ERichMenuAreaActionType.Message:
      return strings.actionTypeOptionMessage;
  }
}

function FieldError({ message }: { message?: string }) {
  if (!message) return null;
  return (
    <p className="mt-1 text-xs text-red-600" role="alert">
      {message}
    </p>
  );
}

export function RichMessageAreaActions({
  areaKeys,
  areas,
  onChange,
  fieldErrors,
  expandAreaSignal,
}: Props) {
  const [openIndex, setOpenIndex] = useState(0);

  useEffect(() => {
    if (expandAreaSignal) setOpenIndex(expandAreaSignal.index);
  }, [expandAreaSignal]);

  const toggle = useCallback((index: number) => {
    setOpenIndex((prev) => (prev === index ? -1 : index));
  }, []);

  return (
    <section className="grid gap-4 rounded-2xl border border-border/60 bg-white p-6">
      <h2 className="text-sm font-semibold text-ink">
        {strings.actionsSectionTitle}
      </h2>
      <div className="grid gap-3">
        {areaKeys.map((key, index) => {
          const action = areas[index] ?? { type: ERichMenuAreaActionType.None };
          const isOpen = openIndex === index;
          const errs = fieldErrors?.[index];

          const selectControl = (
            <select
              className="w-full rounded-lg border border-border/60 bg-white px-3 py-2 text-sm"
              value={action.type}
              onChange={(e) => {
                const type = e.target.value as ERichMessageAreaActionType;
                onChange(index, { type });
                if (type !== ERichMenuAreaActionType.None) {
                  setOpenIndex(index);
                }
              }}
            >
              {actionOptions.map((opt) => (
                <option key={opt} value={opt}>
                  {optionLabel(opt)}
                </option>
              ))}
            </select>
          );

          return (
            <div
              key={key}
              className={cn(
                'rounded-xl border bg-surface/40 p-4',
                isOpen
                  ? 'border-accent/80 ring-1 ring-accent/25'
                  : 'border-border/50',
              )}
            >
              <button
                type="button"
                className="flex w-full items-center justify-between text-left"
                onClick={() => toggle(index)}
              >
                <span className="text-sm font-semibold text-ink">
                  <span className="mr-2 text-muted">{isOpen ? '▾' : '▸'}</span>
                  {key}
                </span>
              </button>

              {!isOpen && <div className="mt-3">{selectControl}</div>}

              {isOpen && (
                <div className="mt-4 grid gap-4">
                  <label className="grid gap-1 text-xs text-muted">
                    {strings.actionTypeOptionUri}
                    {selectControl}
                  </label>

                  {action.type === ERichMenuAreaActionType.Uri && (
                    <>
                      <label className="grid gap-1 text-xs text-muted">
                        URL
                        <Input
                          value={action.uri ?? ''}
                          onChange={(e) =>
                            onChange(index, { ...action, uri: e.target.value })
                          }
                          placeholder={strings.urlPlaceholder}
                          maxLength={RICH_MENU_URI_MAX_LENGTH}
                        />
                        <FieldError message={errs?.uri} />
                      </label>
                      <label className="grid gap-1 text-xs text-muted">
                        Label
                        <Input
                          value={action.label ?? ''}
                          onChange={(e) =>
                            onChange(index, {
                              ...action,
                              label: e.target.value,
                            })
                          }
                          maxLength={RICH_MENU_ACTION_LABEL_MAX_LENGTH}
                        />
                        <FieldError message={errs?.label} />
                      </label>
                    </>
                  )}

                  {action.type === ERichMenuAreaActionType.Message && (
                    <label className="grid gap-1 text-xs text-muted">
                      Text
                      <Textarea
                        rows={3}
                        maxLength={RICH_MENU_MESSAGE_ACTION_TEXT_MAX_LENGTH}
                        value={action.text ?? ''}
                        onChange={(e) =>
                          onChange(index, { ...action, text: e.target.value })
                        }
                        placeholder={strings.messageTextPlaceholder}
                      />
                      <FieldError message={errs?.text} />
                    </label>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
}
